// ─────────────────────────────────────────────────────────────
// SCHOOL SHOW READY™ — all copy and links live here. Image URLs live in data/images.ts, the pixel in data/pixel.ts.
// Same structure as Martial Arts Kids Class Ready / ABA Session Ready (modeled on Managers), with a NEW palette
// (evergreen + coral + gold on cream), NEW button copy and a NEW section (cast-size calculator + songs strip), to test
// what converts.
// Promise (must match ads, checkout and the PDF): the school show year + 10 complete ready-to-perform plays
// (4 Christmas plays by age + 6 shows for the rest of the year), every line written, casts for 10–60 children,
// traditional songs only (no original songs, no backing tracks), $27 one-time, 10-day guarantee, 5 bonuses.
// Values = the checkout: main $159; bonuses $37, $27, $27, $27, $37 ($155); total $314 → $27, you save $287.
// No testimonials, no invented numbers.
// ─────────────────────────────────────────────────────────────

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://school-show-ready.impultienda.ar/checkout';

export const PAGE_TITLE = 'School Show Ready™ | Your Whole Year of School Shows, Already Written';
export const META_DESCRIPTION =
  'The Christmas play and every school show of the year, ready to perform: 10 complete plays with every line written, casts for 10 to 60 children and a 4-week rehearsal plan.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'SCHOOL SHOW READY™';
/** Label for every button that scrolls to the offer section (new copy for this landing) */
export const ctaLabel = 'GET THE 10 PLAYS';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'GET ALL 10 PLAYS · $27';
export const topBarMobileLabel = '10 PLAYS · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR ELEMENTARY, PRIMARY & PRESCHOOL TEACHERS',
  titleLead: 'YOUR WHOLE YEAR OF SCHOOL SHOWS,',
  titleHighlight: 'ALREADY WRITTEN.',
  sub: 'Starting with your Christmas play: 10 ready-to-perform plays with every line written, a cast for 10 to 60 children and a 4-week rehearsal plan.',
  mockupAlt: 'School Show Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '10', label: 'COMPLETE PLAYS' },
  { value: '4', label: 'CHRISTMAS PLAYS BY AGE' },
  { value: '10–60', label: 'CHILDREN PER CAST' },
  { value: '4', label: 'WEEKS TO SHOW DAY' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'MIDNIGHT IN NOVEMBER VS. SHOW DAY.',
  before: {
    label: 'BEFORE',
    when: 'November, 11 PM',
    quote: '“I still don’t have a script for 30 kids.”',
    items: ['Writing lines until midnight.', '30 children and only 8 parts.', 'Nobody knows where to stand.'],
  },
  after: {
    label: 'AFTER',
    when: 'Show day',
    items: ['Choose the play for your age group.', 'Print the script: every line is written.', 'Rehearse 15–20 minutes a day.', 'Proud kids. Happy families.'],
  },
};

// ── 3. WHICH CHRISTMAS PLAY IS YOURS (pick the age, see the play that is ready for it) ──
export const selector = {
  eyebrow: 'START WITH YOUR CHRISTMAS SHOW',
  title: 'WHICH CHRISTMAS PLAY IS YOURS?',
  sub: 'Pick your group and see the play that is ready for it.',
  startLabel: 'YOUR CHRISTMAS PLAY',
  tablistLabel: 'Christmas plays by age',
  columns: ['PAGE 1 · THE PLAY', 'PAGE 2 · SCRIPT PART 1', 'PAGE 3 · SCRIPT PART 2 & REHEARSALS'],
  note: 'All 10 plays follow this same 3-page structure. Adapt each one to your class, your stage and your school’s policies.',
  items: [
    {
      tab: 'Ages 3–5: a gentle nativity',
      n: 'PLAY 01 · AGES 3–5',
      title: 'Who’s in the Stable?',
      goal: 'On a cold night, Mary and Joseph find a little stable full of friendly animals. One by one, the animals welcome them.',
      open: ['About 10 minutes · Mary, Joseph, 3 Kings + 8 animal and angel groups', 'Songs: Twinkle, Twinkle, Little Star · Away in a Manger · We Wish You a Merry Christmas', 'Costumes: animal ears on card headbands, tea towels, tinsel halos and card stars'],
      activity: ['Every line written, like: NARRATOR: “Who’s in the stable?” ALL COWS: “Moo, moo! We are the cows. Come in, come in!”', 'One short line per group, said all together: perfect for the youngest', 'Song cues in the right place, so you always know what comes next'],
      close: ['The visitors and the finale: “All of us! Merry Christmas!”', 'A 4-week rehearsal plan, 15–20 minutes a day', 'A family letter ready to copy, with the costume request'],
    },
    {
      tab: 'Ages 5–7: the classic nativity',
      n: 'PLAY 02 · AGES 5–7',
      title: 'The Innkeeper’s Busy Night',
      goal: 'Bethlehem is so busy that every inn is full… until the youngest innkeeper remembers the stable.',
      open: ['About 15 minutes · 10 main parts + travelers, animals, angels, shepherds and stars', 'Songs: O Little Town of Bethlehem · Away in a Manger · While Shepherds Watched · We Three Kings · Silent Night', 'Set: 3 cardboard “inn doors”, a manger and battery candles'],
      activity: ['Every line written, like: INNKEEPER 3: “Try tomorrow! Try next door! Try anywhere!”', 'YOUNGEST INNKEEPER: “Wait! We have a stable. It’s warm, and the animals are kind.”', 'Narrator lines you can split between 1 and 4 children'],
      close: ['Shepherds, angels, the star and the kings, with every song cue', 'The ending: “There is always room for kindness.”', 'A 4-week rehearsal plan + the family letter'],
    },
    {
      tab: 'Ages 7–9: no nativity, lots of fun',
      n: 'PLAY 03 · AGES 7–9',
      title: 'The Great Letter Mix-Up',
      goal: 'On Christmas Eve, a big wind mixes up every letter at the North Pole Post Office. The last letter shows what the best gift is.',
      open: ['About 20 minutes · a Christmas play with no nativity', 'Sorters, Snowflakes, Reindeer, Santa, Mrs. Claus and the Letter Writer', 'Songs: Jingle Bells · Deck the Halls · Up on the Housetop · We Wish You a Merry Christmas'],
      activity: ['Every line written, like: SORTER 1: “This one wants a pony… but the address is a goldfish bowl!”', 'A big wind scene with flying letters that kids love to perform', 'Up to 6 narrators for your confident readers'],
      close: ['The last letter: a wish that doesn’t fit in a sack', 'SANTA: “The best gifts aren’t in my sack. They’re in you.”', 'A 4-week rehearsal plan + the family letter'],
    },
    {
      tab: 'The whole school together',
      n: 'PLAY 04 · WHOLE SCHOOL',
      title: 'Christmas Around the World',
      goal: 'Two guides take the audience to six countries. Each class shows how Christmas is celebrated there, and the whole school sings at the end.',
      open: ['About 25 minutes · every class presents one country', 'Mexico, Sweden, Germany, Austria, the Philippines and Australia', 'Songs: O Christmas Tree · Silent Night · Jingle Bells · We Wish You a Merry Christmas'],
      activity: ['Every line written, like: CLASS F: “In Australia, Christmas is in summer! Sunhats on!”', 'Each class rehearses its own stop in its own classroom', '2 guides link the stops, so the show flows on its own'],
      close: ['The finale: “Different lights, different songs… the same Christmas joy!”', 'A whole-school plan: classes first, then 2 run-throughs in the hall', 'A family letter ready to copy'],
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages in data/images.ts) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE THE SCRIPTS BEFORE YOU BUY.',
  sub: 'Open any page and read it at full size: your show year, a complete play in 3 pages and your show planner.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full book.',
  captions: [
    'Your school show year',
    'Play 02 · the play',
    'Play 02 · script part 1',
    'Play 02 · script part 2 & rehearsals',
    'Cast any play for 10 to 60 children',
    'Show planner + cast list',
  ],
};

// ── 5. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE SYSTEM FOR EVERY SHOW',
  title: 'THE PLAY IS WRITTEN. YOU JUST CAST IT AND REHEARSE.',
  text: 'Choose the play for the occasion and the age, give out the parts, rehearse 15–20 minutes a day and walk onto the stage in 4 weeks. Every play gives you the play, the full script and the rehearsal plan in 3 pages.',
  steps: [
    { title: 'CHOOSE', text: 'Pick the play for the occasion and the age.' },
    { title: 'CAST', text: 'Give out the parts for 10 to 60 children.' },
    { title: 'REHEARSE', text: '15–20 minutes a day, for 4 weeks.' },
    { title: 'PERFORM', text: 'Costumes on, families in, curtain up.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE PLAY',
  features: [
    { icon: 'goal', title: 'Every line, already written', text: 'Narrator, main part and group lines in order, with stage directions and song cues.' },
    { icon: 'timer', title: 'A 4-week rehearsal plan', text: 'What to rehearse each week, 15–20 minutes a day, up to the dress rehearsal.' },
    { icon: 'steps', title: 'A cast for 10 to 60 children', text: 'Narrators, main parts and groups that grow or shrink with your class.' },
    { icon: 'questions', title: 'Traditional songs only', text: 'Carols and songs your class may already know. No new songs to learn.' },
    { icon: 'quote', title: 'Costumes & set from school things', text: 'Tea towels, card crowns, tinsel, T-shirts and cardboard boxes. No sewing.' },
    { icon: 'action', title: 'A family letter for every play', text: 'Ready to copy: the date, the part and what to send from home.' },
  ],
} as const;

// ── 5b. CAST CALCULATOR + SONGS (new section in this landing) ──────────────────
export const castCalc = {
  eyebrow: 'EVERY CHILD ON STAGE. EVERY CHILD HEARD.',
  title: 'HOW MANY CHILDREN ARE IN YOUR SHOW?',
  sub: 'Tap your class size and see how Play 02, The Innkeeper’s Busy Night, is cast. Every play in the book works the same way.',
  sizesLabel: 'Children in the show',
  roles: { narrators: 'NARRATORS', main: 'MAIN PARTS', groups: 'IN GROUPS' },
  groupsNames: 'Travelers · Animals · Angels · Shepherds · Stars',
  sizes: [
    { n: 10, narrators: 0, main: 10, groups: 0, note: 'You read the narration. The 10 main parts join in the group lines and the songs.' },
    { n: 20, narrators: 2, main: 10, groups: 8, note: '2 narrators, the 10 main parts and 8 children in the 5 groups.' },
    { n: 30, narrators: 4, main: 10, groups: 16, note: '4 narrators, the 10 main parts and groups of 3–4 children.' },
    { n: 45, narrators: 4, main: 10, groups: 31, note: '4 narrators, the 10 main parts and groups of 6–7 children.' },
    { n: 60, narrators: 4, main: 10, groups: 46, note: '4 narrators, the 10 main parts and groups of 9–10 children.' },
  ],
  songsTitle: 'NO NEW SONGS TO LEARN',
  songsText: 'No original songs, no sheet music and no backing tracks: every song in the plays is a traditional carol or song your class may already know, and some shows are spoken only.',
  songs: ['Silent Night', 'Away in a Manger', 'O Little Town of Bethlehem', 'We Three Kings', 'Jingle Bells', 'Deck the Halls', 'We Wish You a Merry Christmas', 'Auld Lang Syne'],
};

// ── 6. THE SCHOOL SHOW YEAR ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR SCHOOL SHOW YEAR',
  title: 'EVERY SHOW OF THE YEAR, MAPPED.',
  sub: 'Month by month: which play to use, when to start rehearsing and when to send the family letter.',
  quarters: [
    {
      q: 'AUTUMN',
      weeks: 'SEP–NOV',
      name: 'HARVEST & THANKSGIVING',
      full: [
        { when: 'OCTOBER', label: 'HARVEST FESTIVAL', title: 'Play 09 · The Little Red Hen’s Harvest' },
        { when: 'NOVEMBER', label: 'THANKSGIVING (US)', title: 'Play 09 · The Little Red Hen’s Harvest' },
      ],
      activities: 'Start rehearsing 4 weeks before the show.',
    },
    {
      q: 'CHRISTMAS',
      weeks: 'NOV–DEC',
      name: 'THE BIG SHOW',
      full: [
        { when: 'WEEK 1 OF NOVEMBER', label: 'START REHEARSING', title: 'Plays 01–04 · one for every age', review: true },
        { when: 'DECEMBER', label: 'CHRISTMAS SHOW', title: 'Ages 3–5 · 5–7 · 7–9 · whole school' },
      ],
      activities: 'Send the family letter and the costume note in week 1.',
    },
    {
      q: 'WINTER & SPRING',
      weeks: 'JAN–APR',
      name: 'FOUR MORE SHOWS',
      full: [
        { when: 'JANUARY', label: 'NEW YEAR', title: 'Play 05 · The Parade of the Months' },
        { when: 'EARLY MARCH', label: 'WORLD BOOK DAY', title: 'Play 06 · The Night the Books Woke Up' },
        { when: 'MARCH–APRIL', label: 'EASTER & SPRING', title: 'Play 07 · The Mystery of the Missing Eggs' },
        { when: 'APRIL 22', label: 'EARTH DAY', title: 'Play 08 · Save Our Park!' },
      ],
      activities: 'Each show has its own start date in the plan.',
    },
    {
      q: 'SUMMER',
      weeks: 'MAY–JUL',
      name: 'THE BIG GOODBYE',
      full: [
        { when: 'MAY', label: 'START REHEARSING', title: 'Play 10 · The Memory Suitcase', review: true },
        { when: 'JUNE–JULY', label: 'END OF YEAR & GRADUATION', title: 'The certificate walk' },
      ],
      activities: 'A thank-you to teachers and families in the script.',
    },
  ],
  toolsTitle: 'PLUS YOUR DIRECTOR TOOLS',
  tools: ['When rehearsals get hard: 8 situations, what to say and do', 'Show planner + cast list: plan any show in 10 minutes', 'Show day field card: the warm-up, the timeline and stage safety'],
  southNote: 'Southern hemisphere schools: match every show to your own term dates.',
};

// ── 7. THE 10 COMPLETE PLAYS ─────────────────────────────────
export const meetings = {
  eyebrow: 'THE 10 COMPLETE PLAYS',
  title: 'TEN PLAYS. READY TO PERFORM.',
  sub: 'Follow the show year, or start with the play you need now. Each one is 3 pages: the play, script part 1, script part 2 & rehearsals.',
  items: [
    { n: '01', tag: 'CHRISTMAS · AGES 3–5', title: 'Who’s in the Stable?', goal: 'A gentle nativity: one by one, the stable animals welcome Mary and Joseph.' },
    { n: '02', tag: 'CHRISTMAS · AGES 5–7', title: 'The Innkeeper’s Busy Night', goal: 'Every inn is full… until the youngest innkeeper remembers the stable.' },
    { n: '03', tag: 'CHRISTMAS · AGES 7–9', title: 'The Great Letter Mix-Up', goal: 'A big wind mixes up every letter at the North Pole Post Office. No nativity.' },
    { n: '04', tag: 'CHRISTMAS · WHOLE SCHOOL', title: 'Christmas Around the World', goal: 'Six countries, six classes, one finale for the whole school.' },
    { n: '05', tag: 'NEW YEAR · AGES 5–9', title: 'The Parade of the Months', goal: 'The twelve months say goodbye to the Old Year, and the New Year arrives with a wish.' },
    { n: '06', tag: 'WORLD BOOK DAY · AGES 6–9', title: 'The Night the Books Woke Up', goal: 'Classic story characters wake up in the library to show that books are never boring.' },
    { n: '07', tag: 'EASTER & SPRING · AGES 4–7', title: 'The Mystery of the Missing Eggs', goal: 'Detective Owl looks for Mother Hen’s eggs, and spring brings a surprise.' },
    { n: '08', tag: 'EARTH DAY · AGES 6–9', title: 'Save Our Park!', goal: 'A class finds its park full of litter and becomes a team of planet heroes.' },
    { n: '09', tag: 'HARVEST & THANKSGIVING · AGES 4–7', title: 'The Little Red Hen’s Harvest', goal: 'The classic tale with a harvest ending about helping, sharing and saying thank you.' },
    { n: '10', tag: 'END OF YEAR & GRADUATION · AGES 5–11', title: 'The Memory Suitcase', goal: 'The year’s memories, a thank-you to teachers and families, and the certificate walk.' },
  ],
  cta: 'GET ALL 10 PLAYS + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 BOOK',
  title: 'EVERYTHING ELSE SHOW DAY NEEDS.',
  sub: 'An express rehearsal plan, poems, printables, a backstage guide and no-sew costumes, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: 'The 2-Week Express Rehearsal Plan',
      hook: 'Running late? Get stage-ready in 2 weeks.',
      title: 'The 2-Week Express Rehearsal Plan — For When the Show Is Almost Here',
      text: 'A day-by-day schedule of 10 short rehearsals, what to rehearse first, how to split the class into small groups, quick tricks to learn lines, tape marks for the stage and a dress rehearsal checklist.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: '30 Poems & Readings for School Shows',
      hook: 'Add a moment to any show or assembly.',
      title: '30 Poems & Readings for School Shows — Short Pieces for Every Season',
      text: '30 short, original poems and readings for young voices, by occasion: harvest, Christmas and winter, the new year, books, spring, our planet, friendship and the end of the year.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: 'Show Day Printables',
      hook: 'Print it, fill it in, done.',
      title: 'Show Day Printables — Tickets, Posters, Programs & Star Performer Certificates',
      text: 'A show poster, family tickets, a program with space for the cast list, a seating plan, a volunteer sign-up sheet, a costume request note, stage name cards and a Star Performer certificate.',
    },
    {
      label: 'BONUS 04',
      value: '$27',
      short: 'Backstage with Little Kids',
      hook: 'Calm, quiet children on show day.',
      title: 'Backstage with Little Kids — The Show Day Run Sheet & Stage Management Guide',
      text: 'A minute-by-minute show day run sheet, how to line up the cast by scene, hand signals for entrances and exits, jobs for helpers and what to do when a child freezes or forgets a line.',
    },
    {
      label: 'BONUS 05',
      value: '$37',
      short: '40 No-Sew Costumes',
      hook: 'No sewing machine. No big costs.',
      title: '40 No-Sew Costumes — Made from Classroom & Home Supplies',
      text: '40 simple costumes made with paper plates, card, tinsel, old T-shirts, pillowcases and tea towels: angels, shepherds, stars, kings, animals, snowflakes, trees, book characters and more.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT SHOW STARTS HERE',
  title: 'EVERY SCHOOL SHOW OF THE YEAR. ALL IN ONE BOOK.',
  kicker: 'THE COMPLETE DIGITAL BOOK',
  name: 'School Show Ready™',
  description: 'Your school show year and 10 complete, ready-to-perform plays, plus five bonuses: an express rehearsal plan, poems and readings, show day printables, a backstage guide and no-sew costumes.',
  mockupAlt: 'Everything included in School Show Ready™',
  includedTitle: 'YOUR BOOK INCLUDES',
  included: [
    'Your school show year: every show mapped, month by month',
    '4 Christmas plays: ages 3–5, 5–7, 7–9 and whole school',
    '6 more shows: New Year, World Book Day, Easter & spring, Earth Day, harvest, end of year & graduation',
    'Every line written, with narrators and song cues',
    'A cast for 10 to 60 children in every play',
    'Rehearsal plan, costumes, set and family letter for every play',
    'Director tools: hard moments, show planner, show day card',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'School Show Ready™ (the show year + 10 complete plays)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · $2.70 a play',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout (new copy for this landing) */
  cta: 'YES! SEND ME THE PLAYS — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. No original songs or backing tracks: every song is a traditional carol or song. Adapt every play to your class, your stage and your school’s policies.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Read the plays and start rehearsing. If it isn't right for your class, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE CURTAIN UP.',
  sub: 'Built for teachers who are not drama teachers. A ready-to-perform play book, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF book: your school show year (every show of the year mapped month by month, with when to start rehearsing) and 10 complete, ready-to-perform plays of 3 pages each, plus director tools and 5 bonuses.',
    },
    {
      q: 'Are all the lines really written?',
      a: 'Yes. Every play has the full script: every narrator, main part and group line, in order, with the stage directions and the song cues. You print it and give out the parts.',
    },
    {
      q: 'What about the songs?',
      a: 'There are no original songs, no sheet music and no backing tracks. The songs are traditional carols and songs, like Silent Night, Away in a Manger, We Three Kings and Jingle Bells, and some shows are spoken only. Sing them with a piano, a guitar or a recording your school is allowed to use.',
    },
    {
      q: 'How many children can take part?',
      a: 'Every play works for 10 to 60 children: 1 to 6 narrators, the main parts and groups of any size. In the whole-school play, each class presents one country.',
    },
    {
      q: 'Is it a religious nativity?',
      a: 'Plays 01 and 02 are traditional nativity plays. Play 03 (The Great Letter Mix-Up) and Play 04 (Christmas Around the World) are Christmas plays without a nativity. The Easter play can be performed as a spring show, and the other shows are not religious.',
    },
    {
      q: 'What ages is it for?',
      a: 'Children from about 3 to 11. Each play shows its age range, and the 4 Christmas plays cover ages 3–5, 5–7, 7–9 and the whole school.',
    },
    {
      q: 'Which countries is it for?',
      a: 'It is written in English for schools in the UK, Ireland, Australia, New Zealand, Canada and the US. Southern hemisphere schools simply match each show to their own term dates.',
    },
    {
      q: 'Can my school perform the plays for families?',
      a: 'Yes. Your purchase lets you perform the plays at your school, for your pupils’ families, as many times as you like. Sharing the book with other schools, redistributing it or reselling it is not included.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'Can I print it?',
      a: 'Yes. The scripts, the show planner and the cast list are ready to print, and the pages are easy to read on a phone or tablet during rehearsals.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT ARE WE DOING FOR THE CHRISTMAS SHOW?”',
  title: 'START REHEARSING TOMORROW. NOT AFTER MIDNIGHT.',
  text: 'Choose the play. Print the script. Rehearse 15–20 minutes a day for 4 weeks, and your class is ready for the stage.',
  microcopy: 'The show year + 10 complete plays + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'SCHOOL SHOW READY™ · Ready-to-perform plays for teachers. Adapt every play to your class, your stage and your school’s policies.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
